import moment from 'moment';
import { NOTIFICATION_STATUS_CODE } from '../../../enums/notification-status-code.enum';
import { TIMERECORDING_TYPE_OF_WORK_CODES } from '../../../models/complex/time-recording-entry.model';
import { NotificationState } from '../../notification/notification-collection.state';
import {
    effectiveStatusForEntry,
    isRemoteWorkCode,
    statusForCode,
} from '../../notification/selectors/notification.selector';
import { ConfirmationTimesheetContext } from '../../utilities/confirmation-timesheet-context';
import { NotificationIdState } from '../../utilities/notification-id.type';
import { TimesheetWorkEntryState } from '../timesheet-collection.state';

/**
 * Derives the timesheet work entries a notification's *recorded statuses* amount to, for a
 * notification that is in progress or to be signed but has no live confirmation yet —
 * mirroring `ConfirmationTimeRecordingModel.addInitialEntries` /
 * `addInitialTSEntriesForNotification`.
 *
 * Deliberately imports nothing from `derive-confirmation-entries.ts`: that file imports this one,
 * and importing back would create a cycle.
 */

/**
 * The key a status-derived row is tracked and addressed by.
 *
 * Namespaced `status:` rather than `derived:` (the confirmation projection's prefix), so the two
 * kinds of synthetic row can never collide with each other or with a committed uuid. Deterministic
 * for the same reason `derivedEntryId` is: recomputation must yield the same keys, or `@for` tears
 * down every row on every recompute.
 *
 * @param notificationId the notification the status was recorded on
 * @param statusCode the status code the row was derived from
 * @returns the synthetic entry key
 */
export function statusEntryId(notificationId: NotificationIdState, statusCode: string): string {
    const { frontId, qmNum, maNum } = notificationId;
    return `status:${frontId}:${qmNum}:${maNum}:${statusCode}`;
}

/**
 * The end of a row whose end is not otherwise known: the row's own start for a paused
 * notification (a zero-length point), or `now` truncated to the minute otherwise — never earlier
 * than `start`.
 *
 * Two notifications can be in progress at once: the app marks every one but the notification
 * actually being worked on as `paused` (see `pausedChangesFor`). Extending a paused notification's
 * trailing row to `now` would overlap the notification that is actually active, so — mirroring the
 * legacy code, which gave every other notification's rows a zero length and let only the active
 * one's row grow — a paused notification's open end never moves past its own start.
 *
 * Truncated to the minute because every instant the technician can see or type works to the
 * minute; see `startOfMinute` in `build-timesheet-rows.ts` for the same reasoning.
 *
 * @param start the row's start
 * @param now the current instant, as an ISO timestamp
 * @returns the row's end, as an ISO timestamp
 */
function openEnd(start: string, now: string): string {
    const truncatedNow = moment(now).startOf('minute').toISOString();
    return moment(truncatedNow).isBefore(moment(start)) ? start : truncatedNow;
}

/**
 * Stamps the fields every status-derived row carries regardless of which status it came from.
 *
 * `isTravelBillable` and `afterHourCallOut` are always `false`: unlike a confirmation's recorded
 * time entries, these rows are not the technician's own answers, so neither can be read off them.
 *
 * @param entry the notification the row is derived from
 * @param context the notification's resolved timesheet context
 * @param statusCode the status code the row represents, used only to key the row
 * @param workCode the work code to book the row against
 * @param start the row's start
 * @param end the row's end
 * @returns the derived work entry
 */
function statusRow(
    entry: NotificationState,
    context: ConfirmationTimesheetContext,
    statusCode: string,
    workCode: string,
    start: string,
    end: string,
): TimesheetWorkEntryState {
    return {
        kind: 'work',
        entryId: statusEntryId(entry.notificationId, statusCode),
        notificationId: entry.notificationId,
        confirmationType: null,
        start,
        end,
        workCode,
        activityType: '',
        meansOfTransport: '',
        distance: 0,
        address: context.address?.id ?? null,
        addressInOutModel: false,
        isTravelBillable: false,
        afterHourCallOut: false,
        customerName: context.customerName,
        notificationType: context.notificationType,
        isConfirmationSubmitted: false,
    };
}

/**
 * The work entries a notification's recorded statuses amount to, for a notification that has no
 * live confirmation to derive from yet.
 *
 * Unlike the legacy `addInitialEntries`, which invented `now`-based defaults for a missing TRAV or
 * OSR status, a missing status here simply produces no row for it — there is nothing recorded to
 * show, and inventing a time would show the technician a row nobody asked for.
 *
 * @param entry the notification to derive rows for
 * @param context the notification's resolved timesheet context
 * @param enableRemoteWork whether config books remote work under its own codes
 * @param now the current instant, as an ISO timestamp — consulted only for a row whose end is not
 * otherwise known, see {@link openEnd}
 * @returns the derived work entries, oldest first
 */
export function deriveStatusWorkEntries(
    entry: NotificationState,
    context: ConfirmationTimesheetContext,
    enableRemoteWork: boolean,
    now: string,
): TimesheetWorkEntryState[] {
    const effectiveStatus = effectiveStatusForEntry(entry);

    if (enableRemoteWork && isRemoteWorkCode(effectiveStatus.statusCode)) {
        // Mirrors `addInitialTSEntriesForRemoteWork`: on-site repair reported remotely (ROSR) and
        // its to-be-signed counterpart (RTOBS) book under ROSR; full remote support (FCC) and its
        // to-be-signed counterpart (FTOBS) book under FCC.
        const workCode =
            effectiveStatus.statusCode === NOTIFICATION_STATUS_CODE.ROSR ||
            effectiveStatus.statusCode === NOTIFICATION_STATUS_CODE.RTOBS
                ? TIMERECORDING_TYPE_OF_WORK_CODES.ROSR
                : TIMERECORDING_TYPE_OF_WORK_CODES.FCC;
        const start = effectiveStatus.startTime;
        return [statusRow(entry, context, effectiveStatus.statusCode, workCode, start, openEnd(start, now))];
    }

    const rows: TimesheetWorkEntryState[] = [];
    const trav = statusForCode(entry, NOTIFICATION_STATUS_CODE.TRAV);
    const osr = statusForCode(entry, NOTIFICATION_STATUS_CODE.OSR);

    if (trav) {
        const travelWorkCode = context.flags.enableStandbyTravel
            ? TIMERECORDING_TYPE_OF_WORK_CODES.TRAVEL_TO_STANDBY
            : TIMERECORDING_TYPE_OF_WORK_CODES.TRTO;
        // The travel-to leg ends where on-site work started, when that is known; otherwise its end
        // is not known yet either, so it takes the open end the same way the on-site row does.
        // Guarded against malformed data: an OSR recorded before its TRAV would otherwise produce a
        // negative-length row, so the end is clamped to the start instead.
        const travelEnd = osr
            ? moment(osr.startTime).isBefore(moment(trav.startTime))
                ? trav.startTime
                : osr.startTime
            : openEnd(trav.startTime, now);
        rows.push(statusRow(entry, context, NOTIFICATION_STATUS_CODE.TRAV, travelWorkCode, trav.startTime, travelEnd));
    }

    if (osr) {
        rows.push(
            statusRow(
                entry,
                context,
                NOTIFICATION_STATUS_CODE.OSR,
                TIMERECORDING_TYPE_OF_WORK_CODES.OSR,
                osr.startTime,
                openEnd(osr.startTime, now),
            ),
        );
    }

    // No travel-back row: `addInitialEntries` adds a zero-length TRBK to the confirmation's own
    // editable form, but a zero-length row is only noise in the timesheet, and the legacy push
    // counterpart this mirrors — `addInitialTSEntriesForNotification` — never added one either.

    return rows;
}
